import React, { useEffect, useState, useCallback } from "react";
import Highcharts from "highcharts";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import rawData from "./data.json";

const ScatterJitterPlot = () => {
  // Unified range controller
  const [range, setRange] = useState([0, 2000]);
  const [inputRange, setInputRange] = useState([0, 2000]); // For typing without jitter

  const MAX_DATA_VALUE = (() => {
    let max = 0;
    Object.values(rawData).forEach((items) => {
      items.forEach((d) => {
        if (d.medicare_percentage > max) max = d.medicare_percentage;
      });
    });
    return max;
  })();

  /** -------------------------------
   * Extract medicare percentages only
   * -------------------------------- */
  const extractMedicareValues = (data) => {
    const result = {};
    Object.keys(data).forEach((code) => {
      result[code] = data[code].map((item) => item.medicare_percentage);
    });
    return result;
  };

  const medicareOnly = extractMedicareValues(rawData);

  /** --------------------------------
   * Filter using new range values
   * -------------------------------- */
  const filterData = (data, [min, max]) => {
    const filtered = {};
    Object.keys(data).forEach((key) => {
      filtered[key] = data[key].filter((value) => value >= min && value <= max);
    });
    return filtered;
  };

  /** --------------------------------
   * Y-axis config (dynamic max)
   * -------------------------------- */
  const getYAxisConfig = (filteredData) => {
    const dynamicMax = Math.max(
      ...Object.values(filteredData).flat(),
      100
    );

    return {
      type: "linear",
      min: 0,
      max: dynamicMax,
      title: {
        text: "Medicare Percentage",
        style: {
          color: "#64748B",
          fontSize: "14px",
          fontWeight: "600",
          fontFamily: "Roboto Flex",
        },
      },
      labels: {
        style: {
          color: "#64748B",
          fontSize: "12px",
          fontWeight: "500",
        },
      },
      gridLineColor: "#F8FAFC",
      gridLineWidth: 1,
      lineColor: "#E2E8F0",
      tickColor: "#E2E8F0",
    };
  };

  const variationConfig = {
    colors: ["#93C5FD", "#6EE7B7", "#FCD34D", "#C4B5FD", "#FCA5A5", "#67E8F9"],
    markerType: "circle",
    markerRadius: 5,
    jitter: { x: 0.35, y: 0 },
  };

  /** -------------------------------
   * DRAW CHART
   * -------------------------------- */
  useEffect(() => {
    const filteredMedicare = filterData(medicareOnly, range);
    const categories = Object.keys(filteredMedicare);
    const yAxisConfig = getYAxisConfig(filteredMedicare);

    const series = categories.map((cat, index) => {
      const points = rawData[cat]
        .filter((item) =>
          filteredMedicare[cat].includes(item.medicare_percentage)
        )
        .map((item) => ({
          x: index,
          y: item.medicare_percentage,
          negotiated: item.negotiated_rates,
          provider: item.provider_name,
        }));

      const color = variationConfig.colors[index % variationConfig.colors.length];

      return {
        name: cat,
        data: points,
        color: color,
        marker: {
          symbol: variationConfig.markerType,
          radius: variationConfig.markerRadius,
          fillColor: color,
          lineColor: "rgba(255, 255, 255, 0.8)",
          lineWidth: 1,
          states: {
            hover: { enabled: true, lineColor: "#FFFFFF", lineWidth: 3 },
          },
        },
      };
    });

    Highcharts.chart("scatter-jitter-container", {
      chart: {
        type: "scatter",
        zoomType: "xy",
        height: 700,
        spacing: [40, 20, 40, 20],
        borderRadius: 8,
      },
      credits: { enabled: false },
      navigation: {
        buttonOptions: { enabled: false },
      },
      legend: { enabled: false },
      title: {
        text: "Medicare Percentage - Cigna",
        style: { color: "#1E293B", fontSize: "24px", fontWeight: "700" },
        align: "left",
      },
      subtitle: {
        text: `Filter Medicare % using range slider or manual inputs`,
        style: { color: "#64748B", fontSize: "14px" },
        align: "left",
      },
      xAxis: {
        categories,
        title: {
          text: "Billing Codes",
          style: { fontSize: "15px", fontWeight: "bold" },
        },
      },
      yAxis: yAxisConfig,
      plotOptions: { scatter: { jitter: variationConfig.jitter } },
      tooltip: {
        useHTML: true,
        backgroundColor: "white",
        borderColor: "#A9A9A9",
        borderRadius: 7,
        style: { fontFamily: "'Roboto Flex'" },
        headerFormat:
          `<span style="font-size: 14px; font-weight: 600">{series.name}</span><br/>` +
          `<span style="color: #E96926;">─────</span><br/>`,
        pointFormatter: function () {
          return `<div>
                    <div style="color: #64748B; font-size: 11px;">MEDICARE %</div>
                    <div style="font-size: 16px; font-weight: 700;">
                      ${Highcharts.numberFormat(this.y, 2, ".", ",")}%
                    </div>
                  </div>
                  <div style="margin-top: 10px; color: #64748B; font-size: 11px;">NEGOTIATED RATE</div>
                  <div style="font-size: 15px; font-weight: 600; color: #1E293B;">
                    $${Highcharts.numberFormat(this.negotiated, 2, ".", ",")}
                  </div>
                  <div style="margin-top: 10px; color: #64748B; font-size: 11px;">PROVIDER</div>
                  <div style="font-size: 14px; font-weight: 600; color: #334155;">
                    ${this.provider}
                  </div>`;
        },
      },
      series,
    });
  }, [range]);

  /** ----------------------------------------
   * Debounce manual input → apply on 500ms
   * ---------------------------------------- */
  const applyInputRange = useCallback(() => {
    let [min, max] = inputRange.map((v) => Number(v) || 0);

    if (min < 0) min = 0;
    if (max < min) max = min;
    if (max > MAX_DATA_VALUE) max = MAX_DATA_VALUE;

    setRange([min, max]);
  }, [inputRange]);

  useEffect(() => {
    const t = setTimeout(applyInputRange, 500);
    return () => clearTimeout(t);
  }, [inputRange, applyInputRange]);

  const resetFilters = () => {
    setRange([0, MAX_DATA_VALUE]);
    setInputRange([0, MAX_DATA_VALUE]);
  };

  return (
    <div style={{ width: "98%", margin: "auto" }}>
      {/* ---------------------------- */}
      {/* Combined Filter Controller   */}
      {/* ---------------------------- */}

      <div
        style={{
          marginBottom: "25px",
          padding: "20px",
          border: "1px solid #E2E8F0",
          borderRadius: "10px",
          background: "#F8FAFC",
        }}
      >
        <h3 style={{ marginBottom: "15px", color: "#1E293B" }}>
          Medicare % Range
        </h3>

        {/* Slider */}
        <Slider
          range
          min={0}
          max={MAX_DATA_VALUE}
          value={range}
          onChange={(val) => {
            setRange(val);
            setInputRange(val);
          }}
          railStyle={{ backgroundColor: "#E2E8F0" }}
          handleStyle={[
            { borderColor: "#3e3e8c", height: 20, width: 20 },
            { borderColor: "#3e3e8c", height: 20, width: 20 },
          ]}
          trackStyle={[{ backgroundColor: "#3e3e8c" }]}
        />

        {/* Manual Inputs */}
        <div style={{ display: "flex", marginTop: "15px", gap: "15px" }}>
          <div>
            <label style={{ fontSize: "13px", color: "#64748B" }}>Min</label>
            <input
              type="number"
              value={inputRange[0]}
              onChange={(e) =>
                setInputRange([e.target.value, inputRange[1]])
              }
              style={{
                width: "120px",
                padding: "8px",
                border: "1px solid #CBD5E1",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: "13px", color: "#64748B" }}>Max</label>
            <input
              type="number"
              value={inputRange[1]}
              onChange={(e) =>
                setInputRange([inputRange[0], e.target.value])
              }
              style={{
                width: "120px",
                padding: "8px",
                border: "1px solid #CBD5E1",
                borderRadius: "6px",
                fontSize: "14px",
              }}
            />
          </div>

          <button
            onClick={resetFilters}
            style={{
              marginLeft: "auto",
              padding: "10px 20px",
              background: "#3e3e8c",
              color: "white",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontWeight: "600",
            }}
          >
            Reset
          </button>
        </div>
      </div>

      <div id="scatter-jitter-container" style={{ height: "700px" }} />
    </div>
  );
};

export default ScatterJitterPlot;
